import { apiRequest } from '@/domains/api';
import type {
  FirmRouteFilter,
  FirmRouteSort,
  IAdminTeamFirm,
  IAdminTeamFirmUnmapped,
  ICreateTeamFirm,
  IFirmParticipants,
  IFirmRoutes,
  IUpdateTeamFirm
} from './team-firm-contracts';

/**
 * survey-api's team firm register and Firms table (SR-213's Teams block, SR-513):
 * `/api/v1/admin/team-firms` — the register's names, the firms by CRD (`routes`), one firm's
 * participants, the firms registrations met that no name holds, a new name, and a name's firm
 * CRDs replaced. The sole reader of that prefix.
 *
 * **Its own module, though its policies are the participant surface's.** The reads are
 * `responses.browse` and the writes `responses.annotate`, which People's guard already mirrors,
 * so the screen sits inside that guard (`team-routes.tsx`). The module is apart because the data
 * is: the register is reference data about firms, owned by staff after the seed.
 *
 * **The register and the unmapped firms are unpaged**: survey-api answers each with a bare array
 * of some tens. The Firms table and a firm's participants are paged, searched and sorted by
 * survey-api.
 *
 * **No delete and no label edit**, because survey-api has neither: a name, once added, is changed
 * only in its firm CRDs. The create's `Location` names a get-by-code route that does not exist;
 * nothing here reads it — the body is the name.
 */

const BASE = '/api/v1/admin/team-firms';

/** The most firms survey-api answers a read of the routes — its `MaxRoutesLimit`. */
export const ROUTES_MAX_LIMIT = 200;

export const teamFirmsApi = {
  /** The whole register, in label order. */
  list(signal?: AbortSignal): Promise<IAdminTeamFirm[]> {
    return apiRequest<IAdminTeamFirm[]>(BASE, { signal });
  },

  /**
   * One page of the Firms table — Wires, Gating or both — `q` matching the name, an other name or
   * its spellings, or the CRD's digits: survey-api's search, order and paging, with both routes'
   * counts under the same `q`.
   */
  routes(
    query: {
      route: FirmRouteFilter;
      q: string;
      sort: FirmRouteSort;
      dir: 'asc' | 'desc';
      offset: number;
      limit: number;
    },
    signal?: AbortSignal
  ): Promise<IFirmRoutes> {
    const search = new URLSearchParams({
      route: query.route,
      sort: query.sort,
      dir: query.dir,
      offset: String(query.offset),
      limit: String(query.limit)
    });
    if (query.q !== '') search.set('q', query.q);
    return apiRequest<IFirmRoutes>(`${BASE}/routes?${search}`, { signal });
  },

  /** The participants whose latest answer names the firm as current employer — teams first. */
  participants(
    finraFirmId: number,
    page: { offset: number; limit: number },
    signal?: AbortSignal
  ): Promise<IFirmParticipants> {
    const search = new URLSearchParams({ offset: String(page.offset), limit: String(page.limit) });
    return apiRequest<IFirmParticipants>(`${BASE}/routes/${finraFirmId}/participants?${search}`, {
      signal
    });
  },

  /** The firms registrations met that no register name holds, most registrations first. */
  unmapped(signal?: AbortSignal): Promise<IAdminTeamFirmUnmapped[]> {
    return apiRequest<IAdminTeamFirmUnmapped[]>(`${BASE}/unmapped`, { signal });
  },

  /**
   * A new name — 201 with it. 400 `invalid-body` names each member it refuses (`label`,
   * `finraFirmIds`); 409 `code-in-use` on `label` is a label or alias the register holds.
   */
  create(body: ICreateTeamFirm): Promise<IAdminTeamFirm> {
    return apiRequest<IAdminTeamFirm>(BASE, { method: 'POST', body });
  },

  /**
   * A name's firm CRDs, its spellings or both — 200 with it; each set given **replaces** the one
   * held. 400 names a member refused, 409 on `aliases` a spelling another name holds; an empty
   * 404 is a code no name holds. The code is a register row's own, never an address's, and survey-api spells
   * every one in `[a-z0-9_]`; it is encoded all the same, since it is spliced into a path.
   */
  update(code: string, body: IUpdateTeamFirm): Promise<IAdminTeamFirm> {
    return apiRequest<IAdminTeamFirm>(`${BASE}/${encodeURIComponent(code)}`, {
      method: 'PATCH',
      body
    });
  }
};
